/**
 * analytics.js — Analytics service for Zenstrust.
 *
 * Wraps Google Analytics 4 (gtag).
 * Custom PostgreSQL footfall tracking is handled separately via useFootfall hook.
 * All GA4 event calls in the app go through this module only.
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/** Returns true when gtag is available and a Measurement ID is configured. */
function isGAReady() {
  return (
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    Boolean(GA_ID)
  );
}

/**
 * Send a GA4 page_view hit.
 * Called automatically from the route-change hook in AppRouter.
 */
export function trackPageView(path, title) {
  if (!isGAReady()) return;
  window.gtag("config", GA_ID, {
    page_path: path,
    page_title: title || document.title,
    send_page_view: true,
  });
}

/**
 * Send a named GA4 event with optional parameters.
 * @param {string} eventName - GA4 event name (snake_case recommended)
 * @param {object} [params]  - Additional event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!isGAReady()) return;
  window.gtag("event", eventName, params);
}

// ── Named event helpers ────────────────────────────────────────────────────

export const analytics = {
  /** Fired after a successful email/password registration. */
  registration(method = "email") {
    trackEvent("sign_up", { method });
  },

  /** Fired after a successful login (any method). */
  login(method = "email") {
    trackEvent("login", { method });
  },

  /** Fired after the user explicitly logs out. */
  logout() {
    trackEvent("logout");
  },

  /** Fired when a consultation / lead form is successfully submitted. */
  consultationSubmit(interest) {
    trackEvent("consultation_submit", { interest });
  },

  /** Fired when an appointment booking is confirmed. */
  appointmentBook(mode) {
    trackEvent("appointment_book", { meeting_mode: mode });
  },

  /** Fired when a KYC document is successfully uploaded. */
  kycDocumentUpload(documentType) {
    trackEvent("kyc_document_upload", { document_type: documentType });
  },

  /** Fired when the Portfolio page finishes loading data. */
  portfolioView() {
    trackEvent("portfolio_view");
  },

  /** Fired for key conversion events. */
  conversion(conversionType, value = null, currency = "INR") {
    const params = { conversion_type: conversionType };
    if (value !== null) {
      params.value = value;
      params.currency = currency;
    }
    trackEvent("conversion", params);
  },
};
